/**
 * «Слова с квоккой» — бесконечная цепочка слов.
 *
 * Квокка называет слово, ученик отвечает словом на его последнюю букву,
 * квокка — словом на последнюю букву ученика, и так далее. Сервер хранит
 * не партию, а только правила: клиент присылает цепочку целиком, поэтому
 * игра переживает перезапуск сервера и не требует базы.
 *
 * Правила хода — чистые функции: клиент проверяет букву мгновенно, сервер
 * перепроверяет ту же функцию перед словарём.
 */

import { z } from 'zod';

import type { CurriculumLevel } from './curriculum';
import type { LanguageCode } from './language';

/**
 * Слово цепочки на изучаемом языке: только буквы алфавита языка, апостроф
 * и дефис внутри («o'clock», «aujourd'hui», «Kaffee-Tasse»). Китайское
 * слово — два и больше иероглифа (词语接龙: цепочка идёт по иероглифам).
 */
const WORD: Readonly<Record<LanguageCode, RegExp>> = {
  en: /^[a-z](?:[a-z'-]*[a-z])?$/,
  de: /^[a-zäöüß](?:[a-zäöüß-]*[a-zäöüß])?$/,
  fr: /^[a-zàâäçéèêëîïôöùûüÿœæ](?:[a-zàâäçéèêëîïôöùûüÿœæ'-]*[a-zàâäçéèêëîïôöùûüÿœæ])?$/,
  ru: /^[а-яё](?:[а-яё-]*[а-яё])?$/,
  zh: /^\p{Script=Han}{2,8}$/u,
  es: /^[a-záéíóúüñ](?:[a-záéíóúüñ-]*[a-záéíóúüñ])?$/,
};

export const WORD_MIN_LENGTH = 2;
export const WORD_MAX_LENGTH = 30;
/** Дольше цепочка не принимается — защита от раздутого запроса. */
export const CHAIN_MAX_LENGTH = 400;

export function normalizeWord(raw: string): string {
  return raw.normalize('NFC').trim().toLowerCase().replace(/[’‘`]/g, "'");
}

/**
 * «Буква» звена — ключ, по которому сравниваются конец одного слова и
 * начало другого. Диакритика не различается (ä = a, é = e, œ = o, ß = s,
 * ё = е, й = и): слов на «ß» не бывает, а «Ärger» после «Banana» честен.
 */
function letterKey(char: string): string {
  switch (char) {
    case 'ß':
      return 's';
    case 'œ':
      return 'o';
    case 'æ':
      return 'a';
    case 'ё':
      return 'е';
    case 'й':
      return 'и';
    default:
      return char.normalize('NFD').replace(/\p{M}/gu, '');
  }
}

/** Буквы слова без дефисов и апострофов; китайский — иероглифы. */
function letters(word: string, language: LanguageCode): readonly string[] {
  const normalized = normalizeWord(word);
  return language === 'zh' ? [...normalized].filter((char) => /\p{Script=Han}/u.test(char)) : [...normalized].filter((char) => /\p{L}/u.test(char));
}

/** По-русски слово на «ь», «ъ» или «ы» продолжают с буквы перед ней. */
const RU_SKIPPED_ENDINGS = new Set(['ь', 'ъ', 'ы']);

/** С чего должно начинаться следующее слово: последняя буква (иероглиф) слова. */
export function requiredLetter(word: string, language: LanguageCode = 'en'): string {
  const chars = letters(word, language);
  if (language === 'zh') return chars.at(-1) ?? '';
  let index = chars.length - 1;
  if (language === 'ru') while (index > 0 && RU_SKIPPED_ENDINGS.has(chars[index] ?? '')) index -= 1;
  const last = chars[index];
  return last === undefined ? '' : letterKey(last);
}

/**
 * Позиция (в символах Unicode) той буквы слова, что задаёт следующий ход, —
 * для подсветки: у «Fuß» это «ß», у «часы» — «с», у «学生» — «生». `-1` — букв нет.
 */
export function requiredLetterIndex(word: string, language: LanguageCode = 'en'): number {
  const chars = [...word];
  const isLetter = (char: string): boolean => (language === 'zh' ? /\p{Script=Han}/u.test(char) : /\p{L}/u.test(char));
  let index = chars.length - 1;
  while (index >= 0 && !isLetter(chars[index] ?? '')) index -= 1;
  if (language === 'ru') while (index > 0 && RU_SKIPPED_ENDINGS.has((chars[index] ?? '').toLowerCase())) index -= 1;
  return index;
}

/** Начало слова в тех же ключах, что и `requiredLetter`. */
export function startLetter(word: string, language: LanguageCode = 'en'): string {
  const first = letters(word, language)[0];
  if (first === undefined) return '';
  return language === 'zh' ? first : letterKey(first);
}

export type ChainRuleViolation = 'INVALID' | 'WRONG_LETTER' | 'USED';

/**
 * Нарушает ли слово правила цепочки — без словаря. `null` — правила
 * соблюдены, и остаётся проверить, существует ли такое слово.
 */
export function chainViolation(chain: readonly string[], raw: string, language: LanguageCode = 'en'): ChainRuleViolation | null {
  const word = normalizeWord(raw);
  if (word.length < WORD_MIN_LENGTH || word.length > WORD_MAX_LENGTH || !WORD[language].test(word)) return 'INVALID';
  const last = chain.at(-1);
  if (last !== undefined && startLetter(word, language) !== requiredLetter(last, language)) return 'WRONG_LETTER';
  if (chain.some((used) => normalizeWord(used) === word)) return 'USED';
  return null;
}

/**
 * Расстояние между словами с опечатками: вставка, удаление, замена и
 * перестановка соседних букв («teh» → «the») стоят по 1 (Дамерау —
 * Левенштейн, вариант optimal string alignment).
 */
export function typoDistance(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[] = Array.from({ length: rows * cols }, () => 0);
  const at = (i: number, j: number): number => d[i * cols + j] ?? 0;
  for (let i = 0; i < rows; i += 1) d[i * cols] = i;
  for (let j = 0; j < cols; j += 1) d[j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(at(i - 1, j) + 1, at(i, j - 1) + 1, at(i - 1, j - 1) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) best = Math.min(best, at(i - 2, j - 2) + 1);
      d[i * cols + j] = best;
    }
  }
  return at(a.length, b.length);
}

/**
 * Сколько опечаток прощается: в коротком слове одна (иначе «cat» превратится
 * во что угодно), в длинном — две; похожее по звучанию длинное слово
 * («comfterble» → «comfortable») — три.
 */
export function maxTypos(word: string, phonetic = false, language: LanguageCode = 'en'): number {
  // Иероглифы набирают через пиньинь: «опечатка» — другой иероглиф, а не буква.
  if (language === 'zh') return 0;
  const base = word.length <= 4 ? 1 : 2;
  return phonetic && word.length >= 6 ? base + 1 : base;
}

/** Слово в партии: с переводом для подсказки по касанию. */
export interface ChainWord {
  readonly word: string;
  /** `null` — перевод недоступен (нет ни в словаре квокки, ни у модели). */
  readonly translation: string | null;
}

/** Начало партии: уровень берётся из профиля ученика на сервере. */
export const WordGameStartSchema = z.object({ userId: z.string().uuid() });

/** Партия: ступень, на которой играет квокка, и её первое слово. */
export interface WordGameStart {
  readonly level: CurriculumLevel;
  readonly word: ChainWord;
}

export const WordGameTurnSchema = z.object({
  userId: z.string().uuid(),
  /** Слова партии по порядку, первое — слово квокки. */
  chain: z.array(z.string().min(1).max(WORD_MAX_LENGTH)).min(1).max(CHAIN_MAX_LENGTH),
  word: z.string().min(1).max(WORD_MAX_LENGTH * 2),
});
export type WordGameTurn = z.infer<typeof WordGameTurnSchema>;

export type WordGameMistake = ChainRuleViolation | 'NOT_A_WORD';

export type WordGameTurnResult =
  | {
      readonly ok: true;
      readonly user: ChainWord;
      /** Ответ квокки; `null` — у неё кончились слова на эту букву, ученик победил. */
      readonly quokka: ChainWord | null;
      /** `false` — словарь был недоступен, слово принято на веру. */
      readonly verified: boolean;
      /**
       * Ученик опечатался, и слово исправлено: здесь — что он написал, а в
       * `user` — какое слово он имел в виду (оно и встаёт в цепочку).
       */
      readonly correctedFrom: string | null;
    }
  | {
      readonly ok: false;
      readonly reason: WordGameMistake;
      /** Буква, которой ждали, — для подсказки после ошибки. */
      readonly expectedLetter: string;
      /** Опечатка исправлена, но исправленное слово уже было в цепочке (`USED`). */
      readonly correctedTo?: string;
    };
